import {
	PROGRAM_DETAIL_HEADER_BREAKPOINT,
	PROGRAM_DETAIL_HEADER_HEIGHT_DESKTOP,
	PROGRAM_DETAIL_HEADER_HEIGHT_MOBILE
} from '../_constants/ap-detail-scroll.constants'

/**
 * Smoothly scrolls to an element by its ID with header offset for annual program detail pages.
 * Calculates the appropriate offset based on viewport width and header height.
 *
 * @param elementId - The ID of the element to scroll to
 *
 * @example
 * ```typescript
 * programDetailScrollToElement('detail-programu')
 * ```
 */
export const programDetailScrollToElement = (elementId: string): void => {
	if (typeof window === 'undefined') return

	setTimeout(() => {
		const element = document.getElementById(elementId)
		const elementOffsetTop = element?.offsetTop

		if (elementOffsetTop) {
			// Use hardcoded values as the header height changes on scroll
			const headerHeight =
				window.innerWidth <= PROGRAM_DETAIL_HEADER_BREAKPOINT
					? PROGRAM_DETAIL_HEADER_HEIGHT_MOBILE
					: PROGRAM_DETAIL_HEADER_HEIGHT_DESKTOP

			window.scrollTo({
				top: elementOffsetTop - headerHeight,
				behavior: 'smooth'
			})
		} else {
			element?.scrollIntoView({ behavior: 'smooth' })
		}
	})
}

/**
 * Scrolls to an element with a custom offset for mobile devices on annual program detail pages.
 * Used for button click handlers with different mobile offset requirements.
 *
 * @param elementId - The ID of the element to scroll to
 * @param mobileOffset - Custom offset in pixels for mobile devices (default: 100)
 *
 * @example
 * ```typescript
 * programDetailScrollToElementWithCustomOffset('detail-programu', 100)
 * ```
 */
export const programDetailScrollToElementWithCustomOffset = (
	elementId: string,
	mobileOffset: number = 100
): void => {
	if (typeof window === 'undefined') return

	const element = document.getElementById(elementId)
	const elementOffsetTop = element?.offsetTop

	if (elementOffsetTop) {
		window.scrollTo({
			top:
				window.innerWidth <= PROGRAM_DETAIL_HEADER_BREAKPOINT
					? elementOffsetTop - mobileOffset
					: elementOffsetTop,
			behavior: 'smooth'
		})
	} else if (element) {
		element.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}
}
